import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import SEOPageLayout from '../components/seo/SEOPageLayout';

const relatedLinks = [
  { to: '/retrouver-information-entreprise', label: 'Pourquoi retrouver une information prend autant de temps' },
  { to: '/clone-collaborateur-entreprise', label: 'Cloner la mémoire d\'un collaborateur : ce que ça signifie' },
];

const CentraliserInformation = () => {
  return (
    <SEOPageLayout
      title="Centraliser l'information en entreprise : pourquoi les outils ne suffisent pas"
      metaTitle="Centraliser l'information en entreprise | Cloneia"
      metaDescription="Vous avez un CRM, des emails, un drive. Et pourtant, l'information reste introuvable. Découvrez pourquoi centraliser ne suffit pas."
      url="/centraliser-information-entreprise"
      readingTime={4}
      subtitle="Le problème n'est pas de centraliser. C'est de capter."
      relatedLinks={relatedLinks}
    >
      <article className="space-y-12 text-[#a1a1aa] leading-relaxed" data-testid="seo-article-centraliser">

        {/* Section 1 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le mirage de la centralisation
          </h2>
          <p>
            Vous avez investi dans un CRM. Vous avez un drive partagé. Vous utilisez une messagerie d'équipe. Vous avez même peut-être un wiki interne.
          </p>
          <p className="mt-4">
            Et malgré tout, quand quelqu'un pose une question simple — "Où en est le dossier Dupont ?" — personne n'a la réponse immédiate.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Parce que centraliser des outils ne centralise pas l'information.
          </p>
        </motion.div>

        {/* Section 2 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le vrai problème : l'information vit dans les échanges
          </h2>
          <p>
            La majorité de l'information utile d'une entreprise ne se trouve pas dans un outil. Elle se trouve dans les échanges : un appel téléphonique, un email, un message, une conversation de couloir.
          </p>
          <p className="mt-4">
            Ces échanges contiennent les détails, le contexte, les engagements. Mais ils ne sont enregistrés nulle part de façon exploitable.
          </p>
          <div className="mt-6 p-6 rounded-xl border border-white/5 bg-[#1a1a2e]/30">
            <p className="text-[#f5f5f5] font-medium mb-2">La réalité</p>
            <p>
              Un commercial promet un délai au téléphone. Un technicien explique une solution par email. Un manager donne une directive dans un message. Toute cette information est réelle, utile, critique — et pourtant, elle n'est "centralisée" nulle part.
            </p>
          </div>
        </motion.div>

        {/* Section 3 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Pourquoi les outils de centralisation échouent
          </h2>
          <p>
            Tous les outils de centralisation partagent le même défaut : ils dépendent de la saisie humaine.
          </p>
          <div className="mt-6 space-y-4">
            <p>
              <span className="text-[#f5f5f5] font-medium">Le CRM</span> — ne contient que ce que quelqu'un a pris le temps de saisir. C'est-à-dire une fraction de la réalité.
            </p>
            <p>
              <span className="text-[#f5f5f5] font-medium">Le drive partagé</span> — contient des documents, mais pas le contexte des échanges qui les entourent.
            </p>
            <p>
              <span className="text-[#f5f5f5] font-medium">La messagerie</span> — contient tout, mais c'est impossible à fouiller efficacement.
            </p>
          </div>
          <p className="mt-6 text-[#f5f5f5] font-medium">
            Le résultat : chaque outil contient un fragment. Aucun ne contient la vérité.
          </p>
        </motion.div>

        {/* Section 4 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Ce qu'il faudrait vraiment
          </h2>
          <p>
            Ce qu'il faudrait, ce n'est pas un outil de plus où tout ranger. C'est un système qui capte l'information automatiquement, là où elle se trouve déjà.
          </p>
          <p className="mt-4">
            Un système qui ne demande aucun effort à vos équipes. Qui capte l'information dans les échanges naturels et la rend accessible instantanément.
          </p>
        </motion.div>

        {/* Section 5 - Cloneia */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            L'approche Cloneia : capter au lieu de centraliser
          </h2>
          <p>
            Cloneia crée un clone mémoire de chaque collaborateur. Ce clone capte automatiquement l'information contenue dans tous les échanges — appels, emails, messages — sans aucune saisie.
          </p>
          <p className="mt-4">
            Vous n'avez plus besoin de centraliser. L'information est déjà là. Il suffit de poser la question.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            "Où en est le dossier Dupont ?" — Réponse en quelques secondes, avec tout le contexte.
          </p>
        </motion.div>

        {/* Section 6 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            La vraie centralisation, c'est l'accès
          </h2>
          <p>
            La centralisation ne sert à rien si l'information n'est pas accessible au moment où on en a besoin.
          </p>
          <p className="mt-4">
            Avec Cloneia, l'information n'est pas rangée quelque part. Elle est accessible partout. Par une simple question. En quelques secondes.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Ce n'est pas un outil de plus. C'est une nouvelle façon d'accéder à l'information de votre entreprise.
          </p>
        </motion.div>

      </article>
    </SEOPageLayout>
  );
};

export default CentraliserInformation;
